import React, { useMemo } from "react";
import { useLingui } from "@lingui/react/macro";
import { calculateDurationH, calculateNormativeBreakTime } from "@/lib/ModelDetails";
import { ModelData } from "@/lib/ModelDetails";
import { niceNumber } from "@/lib/Util";

type Props = {
    model: ModelData;
    breakTimeMin: number;
};

/**
 * ErrorStatsCards component displays statistics and charts for error analysis.
 * It shows average error percentages, overestimates/underestimates, and sample size.
 */
const ErrorStatsCards = ({ model, breakTimeMin = 0 }: Props) => {
    const { t } = useLingui();
    const operations = model.latestOperations || [];

    // Only include records with actual start/end times for error stats (avoids division by zero and ∞%)
    const operationsWithTimeData = useMemo(
        () =>
            operations.filter(
                op => op.actualDateStartTs != null && op.actualDateEndTs != null && op.actualDurationH > 0
            ),
        [operations]
    );

    const getPercent = (value: number, total: number) => (total > 0 ? (value / total) * 100 : 0);
    const getAvgError = (errors: number[]) =>
        errors.length > 0 ? errors.reduce((sum, err) => sum + err, 0) / errors.length : 0;

    const ordersWithEstimates = useMemo(() => {
        return operationsWithTimeData.map(order => {
            // Use specific break time if provided, otherwise calculate normative break time
            const normBreakTime =
                order.breakTime !== undefined
                    ? order.breakTime
                    : calculateNormativeBreakTime(order.actualDurationH * 60, breakTimeMin);

            return {
                ...order,
                normBreakTime,
                estimationModel: calculateDurationH(order.quantity, model.modelSetup, model.modelSpeed, normBreakTime),
                estimationNormative: calculateDurationH(
                    order.quantity,
                    model.normativeSetup,
                    model.normativeSpeed,
                    normBreakTime
                ),
            };
        });
    }, [operationsWithTimeData, model, breakTimeMin]);

    // Calculate aggregated KPIs for error analysis
    const modelErrors = ordersWithEstimates.map(
        order => ((order.estimationModel - order.actualDurationH) / order.actualDurationH) * 100
    );
    const normativeErrors = ordersWithEstimates.map(
        order => ((order.estimationNormative - order.actualDurationH) / order.actualDurationH) * 100
    );
    const avgModelError =
        modelErrors.length > 0 ? modelErrors.reduce((sum, err) => sum + Math.abs(err), 0) / modelErrors.length : 0;
    const avgNormativeError =
        normativeErrors.length > 0
            ? normativeErrors.reduce((sum, err) => sum + Math.abs(err), 0) / normativeErrors.length
            : 0;

    const modelOverestimates = modelErrors.filter(err => err > 0);
    const modelUnderestimates = modelErrors.filter(err => err < 0);
    const normativeOverestimates = normativeErrors.filter(err => err > 0);
    const normativeUnderestimates = normativeErrors.filter(err => err < 0);

    const sampleSize = ordersWithEstimates.length;
    const modelOverestimatePercent = getPercent(modelOverestimates.length, sampleSize);
    const modelUnderestimatePercent = getPercent(modelUnderestimates.length, sampleSize);
    const normativeOverestimatePercent = getPercent(normativeOverestimates.length, sampleSize);
    const normativeUnderestimatePercent = getPercent(normativeUnderestimates.length, sampleSize);

    const avgModelOverestimateError = getAvgError(modelOverestimates);
    const avgModelUnderestimateError = getAvgError(modelUnderestimates);
    const avgNormativeOverestimateError = getAvgError(normativeOverestimates);
    const avgNormativeUnderestimateError = getAvgError(normativeUnderestimates);

    return (
        <div
            className="d-flex flex-responsive mb-3 gap-3"
            style={{ "--min-child-width": "12rem" } as React.CSSProperties}
        >
            <div className="card gap-3">
                <div className="card-title">{t`Average Error %`}</div>
                <div className="card-row align-items-start">
                    <span className="card-label">{t`Model`}:</span>
                    <span className="card-value">{niceNumber(avgModelError, 2)}%</span>
                </div>
                <div className="card-row align-items-start">
                    <span className="card-label">{t`Normative`}:</span>
                    <span className="card-value">{niceNumber(avgNormativeError, 2)}%</span>
                </div>
            </div>
            <div className="card card-success gap-3">
                <div className="card-title">{t`Overestimates`}</div>
                <div className="card-row align-items-start">
                    <span className="card-label">{t`Model`}:</span>
                    <div className="d-flex flex-column align-items-end">
                        <span className="card-value">
                            {modelOverestimates.length}/{sampleSize} ({niceNumber(modelOverestimatePercent, 2)}%)
                        </span>
                        <span className="card-value card-value--tiny">
                            {t`avg`}: +{niceNumber(avgModelOverestimateError, 2)}%
                        </span>
                    </div>
                </div>
                <div className="card-row align-items-start">
                    <span className="card-label">{t`Normative`}:</span>
                    <div className="d-flex flex-column align-items-end">
                        <span className="card-value">
                            {normativeOverestimates.length}/{sampleSize} ({niceNumber(normativeOverestimatePercent, 2)}
                            %)
                        </span>
                        <span className="card-value card-value--tiny">
                            {t`avg`}: +{niceNumber(avgNormativeOverestimateError, 2)}%
                        </span>
                    </div>
                </div>
            </div>
            <div className="card card-error gap-3">
                <div className="card-title">{t`Underestimates`}</div>
                <div className="card-row align-items-start">
                    <span className="card-label">{t`Model`}:</span>
                    <span className="d-flex flex-column align-items-end">
                        <span className="card-value">
                            {modelUnderestimates.length}/{sampleSize} ({niceNumber(modelUnderestimatePercent, 2)}
                            %)
                        </span>
                        <span className="card-value card-value--tiny">
                            {t`avg`}: -{niceNumber(avgModelUnderestimateError, 2)}%
                        </span>
                    </span>
                </div>
                <div className="card-row align-items-start">
                    <span className="card-label">{t`Normative`}:</span>
                    <span className="d-flex flex-column align-items-end">
                        <span className="card-value">
                            {normativeUnderestimates.length}/{sampleSize} (
                            {niceNumber(normativeUnderestimatePercent, 2)}%)
                        </span>
                        <span className="card-value card-value--tiny">
                            {t`avg`}: -{niceNumber(avgNormativeUnderestimateError, 2)}%
                        </span>
                    </span>
                </div>
            </div>

            <div className="card gap-3">
                <div className="card-title">{t`Sample Size`}</div>
                <div className="card-row align-items-start">
                    <span className="card-label">{t`Work Orders`}:</span>
                    <span className="card-value">{sampleSize}</span>
                </div>
            </div>
        </div>
    );
};

export default ErrorStatsCards;
